import { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatApiError, API_BASE, getToken } from "../../lib/api";
import { COLORS } from "../../lib/brand";
import {
  Plus, Pencil, Save, X, ArrowLeft, FileText, Download, Trash2,
  Mail, Phone, MapPin, CalendarDays, Clock, Upload, Briefcase,
} from "lucide-react";
import { toast } from "sonner";

// ─── Constants ──────────────────────────────────────────────────────────────

const BOOKING_SERVICES = [
  "General Mobile Notary",
  "Estate Planning Documents",
  "Loan Document Signing",
  "Remote Online Notarization",
  "Wedding Officiant",
  "Ink Fingerprinting",
  "I-9 Employment Verification",
  "Process Serving",
  "Document Translation",
  "Hospital / Hospice / Nursing Home Visit",
  "Apostille & International Certification",
  "Other",
];

const STATUS_OPTIONS = [
  { value: "new_inquiry",  label: "New Inquiry",  bg: "#F3F3F3",  fg: "#5C5C5C" },
  { value: "confirmed",    label: "Confirmed",    bg: "#D1FAE5",  fg: "#065F46" },
  { value: "in_progress",  label: "In Progress",  bg: "#FEF3C7",  fg: "#92400E" },
  { value: "completed",    label: "Completed",    bg: "#DBEAFE",  fg: "#1E40AF" },
  { value: "cancelled",    label: "Cancelled",    bg: "#FEE2E2",  fg: "#991B1B" },
];

function statusStyle(status) {
  return STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
}

// ─── List Page ───────────────────────────────────────────────────────────────

export function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState("all");
  const [showNew, setShowNew]   = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/admin/service-bookings");
      setBookings(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  const filtered = filter === "all" ? bookings : bookings.filter((b) => b.status === filter);

  return (
    <div className="space-y-6" data-testid="admin-bookings-page">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold text-[#1A1A1A]">Service Bookings</h1>
          <p className="text-sm text-[#5C5C5C] mt-1">Track and manage call-in service requests.</p>
        </div>
        <button
          onClick={() => setShowNew(true)}
          className="inline-flex items-center gap-1 px-4 py-2 rounded-md text-sm font-medium text-white"
          style={{ background: COLORS.maroon }}
          data-testid="new-booking-btn"
        >
          <Plus size={14} /> New Booking (Call-In)
        </button>
      </header>

      {/* Status filter chips */}
      <div className="flex flex-wrap gap-2">
        <FilterChip active={filter === "all"} onClick={() => setFilter("all")} label="All" count={bookings.length} />
        {STATUS_OPTIONS.map((s) => {
          const c = bookings.filter((b) => b.status === s.value).length;
          return (
            <FilterChip
              key={s.value}
              active={filter === s.value}
              onClick={() => setFilter(s.value)}
              label={s.label}
              count={c}
            />
          );
        })}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-[#E8E2D5] shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-6 text-sm text-[#5C5C5C]">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-sm text-[#5C5C5C]">
            No bookings{filter !== "all" ? ` with status "${statusStyle(filter).label}"` : ""}.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#FBF8F1] text-[#5C5C5C] uppercase text-xs tracking-wider">
              <tr>
                <th className="text-left px-5 py-3">Client</th>
                <th className="text-left px-5 py-3">Service</th>
                <th className="text-left px-5 py-3">Date / Time</th>
                <th className="text-left px-5 py-3">Status</th>
                <th className="text-left px-5 py-3">Created</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E8E2D5]">
              {filtered.map((b) => {
                const st = statusStyle(b.status);
                return (
                  <tr key={b.id} className="hover:bg-[#FBF8F1]/50">
                    <td className="px-5 py-4">
                      <div className="font-medium text-[#1A1A1A]">{b.first_name} {b.last_name}</div>
                      <div className="text-xs text-[#5C5C5C]">{b.email}</div>
                    </td>
                    <td className="px-5 py-4 text-[#1A1A1A]">{b.service_type}</td>
                    <td className="px-5 py-4 text-[#5C5C5C] text-xs">
                      {b.preferred_date || "—"}
                      {b.preferred_time && <span className="ml-1">@ {b.preferred_time}</span>}
                    </td>
                    <td className="px-5 py-4">
                      <span
                        className="text-xs px-2.5 py-1 rounded-full font-medium"
                        style={{ background: st.bg, color: st.fg }}
                      >
                        {st.label}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-xs text-[#5C5C5C]">
                      {new Date(b.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <Link
                        to={`/admin/bookings/${b.id}`}
                        className="text-[#800000] hover:text-[#5C0000] font-medium"
                        data-testid={`booking-row-open-${b.id}`}
                      >
                        Open →
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showNew && (
        <NewBookingModal
          onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); load(); }}
        />
      )}
    </div>
  );
}

// ─── New Booking Modal ───────────────────────────────────────────────────────

function NewBookingModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    first_name: "", last_name: "", company: "", email: "", phone: "",
    service_type: "", preferred_date: "", preferred_time: "", location: "", notes: "",
  });
  const [saving, setSaving] = useState(false);

  const u = (patch) => setForm((f) => ({ ...f, ...patch }));

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.phone || !form.service_type) {
      toast.error("Name, email, phone, and service type are required.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/service-bookings", {
        ...form,
        company: form.company || null,
        preferred_date: form.preferred_date || null,
        preferred_time: form.preferred_time || null,
        location: form.location || null,
        notes: form.notes || null,
      });
      toast.success("Booking created");
      onCreated();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="new-booking-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E8E2D5]">
          <h2 className="font-semibold text-lg text-[#1A1A1A]">New Booking — Call-In</h2>
          <button onClick={onClose} className="text-[#5C5C5C] hover:text-[#1A1A1A]" data-testid="close-modal-btn"><X size={20} /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Client info */}
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[#800000] font-semibold mb-3">Client Information</p>
            <div className="grid grid-cols-2 gap-3">
              <ModalField label="First Name *">
                <input className={inp} value={form.first_name} onChange={(e) => u({ first_name: e.target.value })} data-testid="new-booking-first-name" />
              </ModalField>
              <ModalField label="Last Name *">
                <input className={inp} value={form.last_name} onChange={(e) => u({ last_name: e.target.value })} data-testid="new-booking-last-name" />
              </ModalField>
              <ModalField label="Company">
                <input className={inp} value={form.company} onChange={(e) => u({ company: e.target.value })} placeholder="Optional" />
              </ModalField>
              <ModalField label="Phone *">
                <input className={inp} type="tel" value={form.phone} onChange={(e) => u({ phone: e.target.value })} data-testid="new-booking-phone" />
              </ModalField>
              <ModalField label="Email *" span={2}>
                <input className={inp} type="email" value={form.email} onChange={(e) => u({ email: e.target.value })} data-testid="new-booking-email" />
              </ModalField>
            </div>
          </div>

          {/* Service & Appointment */}
          <div>
            <p className="text-[10px] uppercase tracking-wider text-[#800000] font-semibold mb-3">Service & Appointment</p>
            <div className="grid grid-cols-2 gap-3">
              <ModalField label="Service Type *" span={2}>
                <select className={inp} value={form.service_type} onChange={(e) => u({ service_type: e.target.value })} data-testid="new-booking-service-type">
                  <option value="">Select a service...</option>
                  {BOOKING_SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </ModalField>
              <ModalField label="Preferred Date">
                <input className={inp} type="date" value={form.preferred_date} onChange={(e) => u({ preferred_date: e.target.value })} data-testid="new-booking-date" />
              </ModalField>
              <ModalField label="Preferred Time">
                <input className={inp} type="time" value={form.preferred_time} onChange={(e) => u({ preferred_time: e.target.value })} data-testid="new-booking-time" />
              </ModalField>
              <ModalField label="Location / Address" span={2}>
                <input className={inp} value={form.location} onChange={(e) => u({ location: e.target.value })} placeholder="Where should India go?" data-testid="new-booking-location" />
              </ModalField>
              <ModalField label="Notes" span={2}>
                <textarea className={`${inp} min-h-[80px] resize-none`} value={form.notes} onChange={(e) => u({ notes: e.target.value })} placeholder="Document type, special instructions, urgency..." data-testid="new-booking-notes" />
              </ModalField>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-[#E8E2D5] rounded-md text-[#5C5C5C] hover:border-[#800000]">Cancel</button>
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2 text-sm text-white rounded-md font-medium"
              style={{ background: COLORS.maroon }}
              data-testid="submit-new-booking-btn"
            >
              {saving ? "Creating..." : "Create Booking"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const inp = "w-full px-3 py-2 border border-[#E8E2D5] rounded-md text-sm focus:outline-none focus:border-[#800000] focus:ring-1 focus:ring-[#800000]";

function ModalField({ label, children, span = 1 }) {
  return (
    <label className={`block ${span === 2 ? "col-span-2" : ""}`}>
      <span className="text-[10px] uppercase tracking-wider text-[#5C5C5C] block mb-1">{label}</span>
      {children}
    </label>
  );
}

// ─── Detail Page ─────────────────────────────────────────────────────────────

export function AdminBookingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [booking, setBooking]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [editing, setEditing]   = useState(false);
  const [draft, setDraft]       = useState({});
  const [uploading, setUploading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get(`/admin/service-bookings/${id}`);
      setBooking(r.data);
      setDraft(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function patch(payload) {
    setSaving(true);
    try {
      const r = await api.patch(`/admin/service-bookings/${id}`, payload);
      setBooking(r.data);
      setDraft(r.data);
      toast.success("Saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  async function saveEdits() {
    const editable = ["first_name", "last_name", "company", "email", "phone",
      "service_type", "preferred_date", "preferred_time", "location", "notes"];
    const changes = {};
    for (const k of editable) {
      if ((draft[k] ?? "") !== (booking[k] ?? "")) changes[k] = draft[k] || null;
    }
    if (Object.keys(changes).length === 0) { setEditing(false); return; }
    await patch(changes);
    setEditing(false);
  }

  async function uploadFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("purpose", "service_booking");
      const { data } = await api.post("/admin/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const newIds = [...(booking.file_ids || []), data.id];
      await patch({ file_ids: newIds });
      toast.success("File attached");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function removeFile(fileId) {
    const newIds = (booking.file_ids || []).filter((id) => id !== fileId);
    await patch({ file_ids: newIds });
  }

  async function downloadFile(fileId, filename) {
    try {
      const token = getToken();
      const resp = await fetch(`${API_BASE}/files/${fileId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error("Download failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "document";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download file");
    }
  }

  async function handleDelete() {
    if (!window.confirm("Delete this booking? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/service-bookings/${id}`);
      toast.success("Booking deleted");
      navigate("/admin/bookings");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  const ud = (patch) => setDraft((d) => ({ ...d, ...patch }));

  if (loading) return <div className="text-sm text-[#5C5C5C]">Loading...</div>;
  if (!booking) return <div className="text-sm text-[#5C5C5C]">Booking not found.</div>;

  const st = statusStyle(booking.status);

  return (
    <div className="space-y-6" data-testid="admin-booking-detail">
      <button onClick={() => navigate(-1)} className="text-sm text-[#5C5C5C] hover:text-[#800000] flex items-center gap-1">
        <ArrowLeft size={14} /> Back
      </button>

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-[#1A1A1A]">{booking.first_name} {booking.last_name}</h1>
          <div className="text-sm text-[#5C5C5C] mt-1">
            Booking #{booking.id.slice(0, 8)} · created {new Date(booking.created_at).toLocaleString()}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleDelete}
            className="text-sm border border-[#E8E2D5] px-3 py-2 rounded-md flex items-center gap-2 text-red-600 hover:border-red-400"
            data-testid="delete-booking-btn"
          >
            <Trash2 size={14} /> Delete
          </button>
          {!editing ? (
            <button
              onClick={() => setEditing(true)}
              className="text-sm text-white px-3 py-2 rounded-md flex items-center gap-2"
              style={{ background: COLORS.maroon }}
              data-testid="edit-booking-btn"
            >
              <Pencil size={14} /> Edit
            </button>
          ) : (
            <>
              <button onClick={() => { setDraft(booking); setEditing(false); }} className="text-sm border border-[#E8E2D5] px-3 py-2 rounded-md flex items-center gap-2 hover:border-[#800000]" data-testid="cancel-edits-btn">
                <X size={14} /> Cancel
              </button>
              <button onClick={saveEdits} disabled={saving} className="text-sm text-white px-3 py-2 rounded-md flex items-center gap-2" style={{ background: COLORS.maroon }} data-testid="save-edits-btn">
                <Save size={14} /> {saving ? "Saving..." : "Save"}
              </button>
            </>
          )}
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left — details */}
        <div className="lg:col-span-2 space-y-6">
          <Card title="Client">
            {editing ? (
              <div className="grid grid-cols-2 gap-3" data-testid="client-edit-fields">
                <EF label="First Name"><input className={inp} value={draft.first_name || ""} onChange={(e) => ud({ first_name: e.target.value })} /></EF>
                <EF label="Last Name"><input className={inp} value={draft.last_name || ""} onChange={(e) => ud({ last_name: e.target.value })} /></EF>
                <EF label="Company"><input className={inp} value={draft.company || ""} onChange={(e) => ud({ company: e.target.value })} placeholder="Optional" /></EF>
                <EF label="Phone"><input className={inp} value={draft.phone || ""} onChange={(e) => ud({ phone: e.target.value })} /></EF>
                <EF label="Email" span={2}><input type="email" className={inp} value={draft.email || ""} onChange={(e) => ud({ email: e.target.value })} /></EF>
              </div>
            ) : (
              <>
                <Row icon={<Mail size={14} />} value={booking.email} />
                <Row icon={<Phone size={14} />} value={booking.phone} />
                {booking.company && <Row label="Company" value={booking.company} />}
              </>
            )}
          </Card>

          <Card title="Service & Appointment">
            {editing ? (
              <div className="grid grid-cols-2 gap-3" data-testid="service-edit-fields">
                <EF label="Service Type" span={2}>
                  <select className={inp} value={draft.service_type || ""} onChange={(e) => ud({ service_type: e.target.value })}>
                    {BOOKING_SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </EF>
                <EF label="Preferred Date"><input type="date" className={inp} value={draft.preferred_date || ""} onChange={(e) => ud({ preferred_date: e.target.value })} /></EF>
                <EF label="Preferred Time"><input type="time" className={inp} value={draft.preferred_time || ""} onChange={(e) => ud({ preferred_time: e.target.value })} /></EF>
                <EF label="Location / Address" span={2}><input className={inp} value={draft.location || ""} onChange={(e) => ud({ location: e.target.value })} /></EF>
                <EF label="Client Notes" span={2}><textarea className={`${inp} min-h-[80px] resize-none`} value={draft.notes || ""} onChange={(e) => ud({ notes: e.target.value })} /></EF>
              </div>
            ) : (
              <>
                <Row icon={<Briefcase size={14} />} label="Service" value={booking.service_type} />
                {booking.preferred_date && <Row icon={<CalendarDays size={14} />} label="Date" value={booking.preferred_date} />}
                {booking.preferred_time && <Row icon={<Clock size={14} />} label="Time" value={booking.preferred_time} />}
                {booking.location && <Row icon={<MapPin size={14} />} label="Location" value={booking.location} />}
                {booking.notes && <Row label="Notes" value={booking.notes} />}
              </>
            )}
          </Card>

          <Card title={`Documents (${(booking.files || []).length})`}>
            {(booking.files || []).length === 0 ? (
              <div className="text-sm text-[#5C5C5C]">No files attached yet.</div>
            ) : (
              <ul className="space-y-2 mb-4">
                {booking.files.map((f) => (
                  <li key={f.id} className="flex items-center justify-between border border-[#E8E2D5] rounded-md px-3 py-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText size={16} className="text-[#800000] shrink-0" />
                      <span className="text-sm truncate">{f.original_filename}</span>
                      <span className="text-xs text-[#5C5C5C]">{(f.size / 1024 / 1024).toFixed(2)} MB</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => downloadFile(f.id, f.original_filename)} className="text-sm text-[#800000] hover:text-[#5C0000] flex items-center gap-1" data-testid={`download-file-${f.id}`}>
                        <Download size={14} />
                      </button>
                      <button onClick={() => removeFile(f.id)} className="text-red-500 hover:text-red-700" data-testid={`remove-file-${f.id}`}>
                        <X size={14} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="text-sm border border-dashed border-[#D4AF37] text-[#800000] px-4 py-2 rounded-md flex items-center gap-2 hover:bg-[#FBF8F1] transition-colors"
              data-testid="upload-file-btn"
            >
              <Upload size={14} /> {uploading ? "Uploading..." : "Attach Document"}
            </button>
            <input ref={fileRef} type="file" hidden onChange={uploadFile} data-testid="file-input" />
          </Card>
        </div>

        {/* Right — sidebar */}
        <div className="space-y-6">
          <Card title="Status">
            <div
              className="inline-block text-sm px-3 py-1 rounded-full font-medium mb-4"
              style={{ background: st.bg, color: st.fg }}
            >
              {st.label}
            </div>
            <label className="block text-xs uppercase tracking-wider text-[#5C5C5C] mb-1">Update Status</label>
            <select
              value={booking.status}
              onChange={(e) => patch({ status: e.target.value })}
              className="w-full px-3 py-2 border border-[#E8E2D5] rounded-md text-sm"
              disabled={saving}
              data-testid="booking-status-select"
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </Card>

          <Card title="Admin Notes">
            <textarea
              defaultValue={booking.admin_notes || ""}
              onBlur={(e) =>
                e.target.value !== (booking.admin_notes || "") &&
                patch({ admin_notes: e.target.value })
              }
              placeholder="Internal notes — not visible to clients."
              className="w-full px-3 py-2 border border-[#E8E2D5] rounded-md text-sm min-h-[120px] resize-none focus:outline-none focus:border-[#800000]"
              data-testid="booking-admin-notes"
            />
          </Card>

          <Card title="Timestamps">
            <Row label="Created" value={new Date(booking.created_at).toLocaleString()} />
            {booking.updated_at && <Row label="Updated" value={new Date(booking.updated_at).toLocaleString()} />}
          </Card>
        </div>
      </div>
    </div>
  );
}

// ─── Shared helpers ───────────────────────────────────────────────────────────

function Card({ title, children }) {
  return (
    <section className="bg-white rounded-xl border border-[#E8E2D5] shadow-sm">
      <div className="px-5 py-4 border-b border-[#E8E2D5]">
        <h3 className="font-medium text-[#1A1A1A]">{title}</h3>
      </div>
      <div className="px-5 py-4 space-y-2">{children}</div>
    </section>
  );
}

function Row({ icon, label, value }) {
  return (
    <div className="flex items-start gap-2 text-sm">
      {icon && <span className="text-[#5C5C5C] mt-0.5 shrink-0">{icon}</span>}
      {label && <span className="text-[#5C5C5C] w-24 shrink-0">{label}</span>}
      <span className="text-[#1A1A1A] break-words">{value || "—"}</span>
    </div>
  );
}

function FilterChip({ active, onClick, label, count }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
        active ? "bg-[#800000] text-white border-[#800000]" : "bg-white text-[#5C5C5C] border-[#E8E2D5] hover:border-[#800000]"
      }`}
    >
      {label} <span className="opacity-70">· {count}</span>
    </button>
  );
}

function EF({ label, children, span = 1 }) {
  return (
    <label className={`block ${span === 2 ? "col-span-2" : ""}`}>
      <span className="text-[10px] uppercase tracking-wider text-[#5C5C5C] block mb-1">{label}</span>
      {children}
    </label>
  );
}
